import { makeObservable, observable, action, flow } from "mobx";
import { API } from "aws-amplify";
// import { getGroups, getSettings } from "../../graphql/queries"; // Commented out - GraphQL removed
import ProfileStore from "../profile/ProfileStore";
import ListUserStore from "../users/pages/ListUserStore";
import { AdvancedSettingsAssignmentTypes } from "../../utils/enums";

class AdvancedSettingsStore {
  defaultUserId = null;
  defaultUser = null;
  selectedUserId = null;
  selectedUser = null;
  isError = false;
  errorMessage = "";
  
  selectedUserIds = [];
  selectedGroupIds = [];
  selectedRoleIds = [];

  selectedContactOwnerRoles = [];
  
  marketingStore = null;

  usersItems = [];
  groupsItems = [];
  rolesItems = [];

  isLoadingUsers = false;
  isLoadingGroups = false;
  isLoadingRoles = false;

  hasLoadedUsers = false;
  hasLoadedGroups = false;
  hasLoadedRoles = false;

  userSelectorEnabled = false;
  groupSelectorEnabled = false;
  roleSelectorEnabled = false;

  stickyAssignment = false;

  selectorsConfig = {
    user: {
      enabledProp: 'userSelectorEnabled',
      selectedIdsProp: 'selectedUserIds'
    },
    group: {
      enabledProp: 'groupSelectorEnabled',
      selectedIdsProp: 'selectedGroupIds'
    },
    role: {
      enabledProp: 'roleSelectorEnabled',
      selectedIdsProp: 'selectedRoleIds'
    }
  };

  selectedAssignmentTypes = {
    [AdvancedSettingsAssignmentTypes.CONTACT]: false,
    [AdvancedSettingsAssignmentTypes.DEFAULT]: false,
    [AdvancedSettingsAssignmentTypes.SINGLE]: false,
    [AdvancedSettingsAssignmentTypes.CUSTOM]: false
  };
  userPhotosCache = {};
  isLoadingUserPhoto = false;
  isLoadingMultiplePhotos = false;
  profileStore = null;
  listUserStore = null;

  constructor({ hitl_selection, owner, marketingStore }) {
    this.profileStore = new ProfileStore();
    this.listUserStore = new ListUserStore();
    this.marketingStore = marketingStore;

    makeObservable(this, {
      defaultUserId: observable,
      defaultUser: observable,
      selectedUserId: observable,
      selectedUser: observable,
      isError: observable,
      errorMessage: observable,
      selectedUserIds: observable,
      selectedGroupIds: observable,
      selectedRoleIds: observable,
      selectedContactOwnerRoles: observable,
      marketingStore: observable,

      usersItems: observable,
      groupsItems: observable,
      rolesItems: observable,
      isLoadingUsers: observable,
      isLoadingGroups: observable,
      isLoadingRoles: observable,
      hasLoadedUsers: observable,
      hasLoadedGroups: observable,
      hasLoadedRoles: observable,

      userSelectorEnabled: observable,
      groupSelectorEnabled: observable,
      roleSelectorEnabled: observable,
      stickyAssignment: observable,
      selectorsConfig: observable,
      selectedAssignmentTypes: observable,
      userPhotosCache: observable,
      isLoadingUserPhoto: observable,
      profileStore: observable,
      listUserStore: observable,
      setSelectedAssignmentType: action,
      handleAssignmentChange: action,
      setSelectedUser: action,
      setSelectedUserIds: action,
      setSelectedGroupIds: action,
      setSelectedRoleIds: action,
      setSelectorEnabled: action,
      setStickyAssignment: action,
      setDefaultUser: action,
      loadUserPhoto: flow,
      setProfileStore: action,
      setIsError: action,
      generateHitlSelection: action,
      setSelectedContactOwnerRoles: action,
      handleContactOwnerRoleSelect: action,
      updateHitlSelection: action,
      loadAllRequiredData: action,

      fetchUsers: flow,
      fetchGroups: flow,
      fetchRoles: flow,

      init: action,
      loadUserPhotos: flow
    });

    this.init(hitl_selection, owner);
  }

  /**
   * Initializes store state based on hitl_selection and owner configuration
   * Sets up assignment types and selected users/roles based on existing config
   * @param {Object} hitl_selection - Human-in-the-loop configuration
   * @param {string} owner - Campaign owner
   */
  init(hitl_selection, owner) {
    const hasHitlSelection = hitl_selection && Object.keys(hitl_selection).length > 0;

    Object.keys(this.selectedAssignmentTypes).forEach(type => {
      this.selectedAssignmentTypes[type] = false;
    });

    if (!hasHitlSelection) {
      if (owner) {
        this.setDefaultUser(owner);
        this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.DEFAULT] = true;
      }
      return;
    }

    const {
      default: defaultSelection,
      assigned_user: assignedUser,
      round_robin: roundRobin,
      contact: contactSelection,
      assigned_owner: assignedOwner
    } = hitl_selection;

    if (defaultSelection) {
      this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.DEFAULT] = true;
      this.setDefaultUser(owner || defaultSelection.selection);
    }

    if (assignedUser) {
      this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.SINGLE] = true;
      this.setSelectedUser(assignedUser.selection);
    }

    if (roundRobin) {
      this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CUSTOM] = true;

      this.setStickyAssignment(roundRobin.sticky || false);

      const users = roundRobin.selection.filter(item => item.entity === 'user').map(({ id }) => id);
      const groups = roundRobin.selection.filter(item => item.entity === 'group').map(({ id }) => id);
      const roles = roundRobin.selection.filter(item => item.entity === 'role').map(({ id }) => id);

      if (users.length > 0) {
        this.setSelectorEnabled('user', true);
        this.setSelectedUserIds(users);
      }

      if (groups.length > 0) {
        this.setSelectorEnabled('group', true);
        this.setSelectedGroupIds(groups);
      }

      if (roles.length > 0) {
        this.setSelectorEnabled('role', true);
        this.setSelectedRoleIds(roles);
      }
    }

    const hasContactOwnerSelections = assignedOwner &&
                                     Array.isArray(assignedOwner.selection) && 
                                     assignedOwner.selection.length > 0;
    
    if (contactSelection || hasContactOwnerSelections) {
      this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CONTACT] = true;
      
      if (hasContactOwnerSelections) {
        this.selectedContactOwnerRoles = assignedOwner.selection;
      }
    } else {
      this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CONTACT] = false;
    }
  }

  /**
   * Loads all data required for advanced settings configuration
   * @param {string} customerId - Customer ID
   */
  loadAllRequiredData(customerId) {
    if (!customerId) return;

    this.fetchUsers(customerId);
    this.fetchRoles(customerId);
    this.fetchGroups(customerId);
  }

  setProfileStore(profileStore) {
    this.profileStore = profileStore;
  }

  /**
   * Sets the assignment type and cleans associated data if deactivated
   * @param {string} type - Assignment type to change
   * @param {boolean} isActive - Whether to activate or deactivate
   */
  setSelectedAssignmentType(type, isActive = true) {
    this.selectedAssignmentTypes[type] = isActive;

    if (!isActive) {
      if (type === AdvancedSettingsAssignmentTypes.SINGLE) {
        this.selectedUserId = null;
        this.selectedUser = null;
      } else if (type === AdvancedSettingsAssignmentTypes.CUSTOM) {
        Object.keys(this.selectorsConfig).forEach(selectorType => {
          this.setSelectorEnabled(selectorType, false);
        });
      } else if (type === AdvancedSettingsAssignmentTypes.DEFAULT) {
        this.defaultUserId = null;
        this.defaultUser = null;
      }
    }
  }

  /**
   * Handles changes to assignment type switches
   * @param {string} assignmentType - Assignment type
   * @param {boolean} isChecked - Switch state
   * @param {Object} customer - Selected customer
   * @returns {boolean} Success of operation
   */
  handleAssignmentChange(assignmentType, isChecked, customer) {
    if (assignmentType === AdvancedSettingsAssignmentTypes.DEFAULT && !isChecked) {
      return false;
    }

    if (isChecked && assignmentType === AdvancedSettingsAssignmentTypes.DEFAULT && !this.defaultUser) {
      return false;
    }

    if (isChecked && assignmentType === AdvancedSettingsAssignmentTypes.CONTACT && 
        (!this.rolesItems || this.rolesItems.length === 0)) {
      this.isError = true;
      this.errorMessage = "No roles available";
      return false;
    }

    this.setSelectedAssignmentType(assignmentType, isChecked);

    if (isChecked && assignmentType === AdvancedSettingsAssignmentTypes.DEFAULT && customer && !this.defaultUser) {
      this.setDefaultUser(customer);
    }

    if (!isChecked && assignmentType === AdvancedSettingsAssignmentTypes.SINGLE) {
      this.setSelectedUser(null);
    }

    if (!isChecked && assignmentType === AdvancedSettingsAssignmentTypes.CUSTOM) {
      this.setSelectedUserIds([]);
      this.setSelectedGroupIds([]);
      this.setSelectedRoleIds([]);
    }

    if (assignmentType === AdvancedSettingsAssignmentTypes.CONTACT) {
      if (isChecked) {
        if (this.rolesItems.length > 0) {
          const allRoleIds = this.rolesItems.map(role => role.id);
          this.setSelectedContactOwnerRoles(allRoleIds);
        }
      } else {
        this.setSelectedContactOwnerRoles([]);
      }
    }

    this.updateHitlSelection();
    return true;
  }

  /**
   * Handles toggling of Contact Owner roles
   * @param {string} roleId - Role ID to toggle
   */
  handleContactOwnerRoleSelect(roleId) {
    const currentRoles = [...this.selectedContactOwnerRoles];
    const index = currentRoles.indexOf(roleId);
    
    if (index === -1) {
      currentRoles.push(roleId);
      this.setSelectedContactOwnerRoles(currentRoles);
      this.updateHitlSelection();
    } else {
      if (currentRoles.length === 1) {
        this.isError = true;
        this.errorMessage = "You must select at least one contact owner role";
      } else {
        currentRoles.splice(index, 1);
        this.setSelectedContactOwnerRoles(currentRoles);
        this.updateHitlSelection();
      }
    }
  }

  /**
   * Updates HITL selection in the marketing store
   */
  updateHitlSelection() {
    if (this.marketingStore) {
      const hitlSelection = this.generateHitlSelection();
      this.marketingStore.setHitlSelection(hitlSelection);
    }
  }

  /**
   * Sets the user for Single User assignment
   * @param {string} userId - User ID to set
   */
  setSelectedUser(userId) {
    this.selectedUserId = userId;
    if (userId && this.usersItems.length > 0) {
      this.selectedUser = this.usersItems.find(user => user.id === userId) || null;
    } else {
      this.selectedUser = null;
    }
    this.updateHitlSelection();
  }

  /**
   * Sets the default user for assignment
   * @param {string} userId - User ID to set as default
   */
  setDefaultUser(userId) {
    this.defaultUserId = userId;
    if (userId && this.usersItems.length > 0) {
      this.defaultUser = this.usersItems.find(user => user.id === userId) || null;

      if (this.defaultUser) {
        this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.DEFAULT] = true;
      }
    } else {
      this.defaultUser = null;
      this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.DEFAULT] = false;
    }
    this.updateHitlSelection();
  }

  setSelectedUserIds(userIds) {
    this.selectedUserIds = userIds;
    this.updateHitlSelection();
  }

  setSelectedGroupIds(groupIds) {
    this.selectedGroupIds = groupIds;
    this.updateHitlSelection();
  }

  setSelectedRoleIds(roleIds) {
    this.selectedRoleIds = roleIds;
    this.updateHitlSelection();
  }

  /**
   * Enables/disables a selector and clears selection if disabled
   * @param {string} type - Selector type (user, group, role)
   * @param {boolean} enabled - Whether to enable or disable
   */
  setSelectorEnabled(type, enabled) {
    const config = this.selectorsConfig[type];

    if (!config) {
      this.isError = true;
      this.errorMessage = `Unknown selector type: ${type}`;
      return;
    }

    this[config.enabledProp] = enabled;

    if (!enabled) {
      this[config.selectedIdsProp] = [];
    }
    this.updateHitlSelection();
  }

  setStickyAssignment(enabled) {
    this.stickyAssignment = enabled;
    this.updateHitlSelection();
  }

  setSelectedContactOwnerRoles(roleIds) {
    this.selectedContactOwnerRoles = roleIds;
    this.updateHitlSelection();
  }

  *loadUserPhoto(userId, customerId, display_photo_url) {
    if (this.userPhotosCache[userId]) {
      return this.userPhotosCache[userId];
    }
    if (!display_photo_url || !this.profileStore) {
      return null;
    }
    try {
      this.isLoadingUserPhoto = true;
      this.profileStore.updateUserData('customer', customerId);
      this.profileStore.updateUserData('id', userId);
      this.profileStore.updateUserData('display_photo_url', display_photo_url);
      yield this.profileStore.loadFile();
      if (this.profileStore.displayPhotoURL) {
        this.userPhotosCache[userId] = this.profileStore.displayPhotoURL;
        return this.profileStore.displayPhotoURL;
      }
      return null;
    } catch (error) {
      this.isError = true;
      this.errorMessage = "Error loading users photos";
      return null;
    } finally {
      this.isLoadingUserPhoto = false;
    }
  }

  /**
   * Fetches users from the customer
   * @param {string} customerId - Customer ID
   * @returns {Array} List of users
   */
  *fetchUsers(customerId) {
    if (this.hasLoadedUsers && this.usersItems.length > 0) return this.usersItems;

    try {
      this.isLoadingUsers = true;

      yield this.listUserStore.onFetchUsers(customerId);
      const users = Array.isArray(this.listUserStore.users) ? this.listUserStore.users : [];

      this.usersItems = users;

      if (this.selectedUserId) {
        this.setSelectedUser(this.selectedUserId);
      }
      if (this.defaultUserId) {
        this.setDefaultUser(this.defaultUserId);
      }

      this.hasLoadedUsers = true;
      return this.usersItems;
    } catch (error) {
      this.isError = true;
      this.errorMessage = "Error fetching users";
      this.usersItems = [];
      return [];
    } finally {
      this.isLoadingUsers = false;
    }
  }

  /**
   * Fetches groups from the customer
   * @param {string} customerId - Customer ID
   * @returns {Array} List of groups
   */
  *fetchGroups(customerId) {
    if (this.hasLoadedGroups && this.groupsItems.length > 0) return this.groupsItems;

    try {
      this.isLoadingGroups = true;

      // GraphQL removed - commented out
      // const groupsResponse = yield API.graphql({
      //   query: getGroups,
      //   variables: { input: { customer_id: customerId } },
      //   authMode: 'AMAZON_COGNITO_USER_POOLS'
      // });

      // const groups = JSON.parse(groupsResponse.data?.getGroups?.body || '[]');
      // this.groupsItems = groups;
      // TODO: Replace with REST API call
      this.groupsItems = [];

      this.hasLoadedGroups = true;
      return this.groupsItems;
    } catch (error) {
      this.isError = true;
      this.errorMessage = "Error fetching groups";
      this.groupsItems = [];
      return [];
    } finally {
      this.isLoadingGroups = false;
    }
  }

  /**
   * Fetches roles from the customer and sets up Contact Owner roles
   * @param {string} customerId - Customer ID
   * @returns {Array} List of roles
   */
  *fetchRoles(customerId) {
    if (this.hasLoadedRoles && this.rolesItems.length > 0) return this.rolesItems;

    try {
      this.isLoadingRoles = true;

      // GraphQL removed - commented out
      // const rolesResponse = yield API.graphql({
      //   query: getSettings,
      //   variables: { input:
      //         { customer: customerId,
      //           entity_id: customerId,
      //           entity_type: "customer",
      //           settings_type: "account_roles"
      //         }
      //   },
      //   authMode: 'AMAZON_COGNITO_USER_POOLS'
      // });

      // const responseBody = rolesResponse.data?.getSettings?.body || '[]';

      // let rolesData = [];
      // let roles = [];

      // try {
      //   rolesData = JSON.parse(responseBody);

      //   if (rolesData.length > 0 && rolesData[0].metadata) {
      //     roles = Object.entries(rolesData[0].metadata).map(([id, name]) => ({
      //       id,
      //       name
      //     }));
      //   }
      // } catch (error) {
      //   this.isError = true;
      //   this.errorMessage = "Error reading roles response";
      // }

      // TODO: Replace with REST API call
      let roles = [];

      this.rolesItems = roles;
      this.hasLoadedRoles = true;

      if (roles && roles.length === 0) {
        this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CONTACT] = false;
        this.selectedContactOwnerRoles = [];
        this.isError = true;
        this.errorMessage = "No roles available";
        this.updateHitlSelection();
      } else if (this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CONTACT] && this.selectedContactOwnerRoles.length === 0) {
        const allRoleIds = roles.map(role => role.id);
        this.setSelectedContactOwnerRoles(allRoleIds);
      }

      return this.rolesItems;
    } catch (error) {
      this.isError = true;
      this.errorMessage = "Error fetching roles";
      this.hasLoadedRoles = false;
      this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CONTACT] = false;
      this.selectedContactOwnerRoles = [];
      return [];
    } finally {
      this.isLoadingRoles = false;
    }
  }

  setIsError(error){
    this.isError = error;
  }

  /**
   * Generates HITL selection configuration based on current settings
   * @returns {Object|null} HITL selection configuration
   */
  generateHitlSelection() {
    const result = {};

    if (this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CONTACT]) {
      result.assigned_owner = {
        priority: 1,
        selection: this.selectedContactOwnerRoles
      };
    } else {
      result.assigned_owner = {
        priority: 1,
        selection: []
      };
    }

    const hasCustomSelections = Object.values(this.selectorsConfig).some(config => {
      return this[config.selectedIdsProp]?.length > 0;
    });

    if (this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CUSTOM] && hasCustomSelections) {
      const selection = [];

      Object.entries(this.selectorsConfig).forEach(([type, config]) => {
        const isEnabled = this[config.enabledProp];
        const selectedIds = this[config.selectedIdsProp];

        if (isEnabled && selectedIds.length > 0) {
          const entities = selectedIds.map(id => ({ id, entity: type }));
          selection.push(...entities);
        }
      });

      if (selection.length > 0) {
        result.round_robin = {
          priority: 2,
          sticky: this.stickyAssignment,
          selection: selection
        };
      }
    }

    if (this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.SINGLE] && this.selectedUserId) {
      result.assigned_user = {
        priority: 3,
        selection: this.selectedUserId
      };
    }

    if (this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.DEFAULT] && this.defaultUserId) {
      result.default = {
        priority: 4,
        selection: this.defaultUserId
      };
    } else {
      this.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.DEFAULT] = false;
    }

    return Object.keys(result).length > 0 ? result : null;
  }

  *loadUserPhotos(users, customerId) {
    if (!users?.length) return this.userPhotosCache;

    try {
      this.isLoadingMultiplePhotos = true;

      const photoPromises = users
        .filter(user => user.display_photo_url)
        .map(async (user) => {
          try {
            const photoUrl = await this.loadUserPhoto(
              user.id,
              customerId,
              user.display_photo_url
            );
            return { userId: user.id, photoUrl };
          } catch (error) {
            return { userId: user.id, photoUrl: user.photo_url };
          }
        });

      const results = yield Promise.all(photoPromises);

      results.forEach(({ userId, photoUrl }) => {
        if (photoUrl) {
          this.userPhotosCache[userId] = photoUrl;
        }
      });

      return this.userPhotosCache;
    } catch (error) {
      this.setError("Error loading user photos");
      return this.userPhotosCache;
    } finally {
      this.isLoadingMultiplePhotos = false;
    }
  }

  setError(message) {
    this.isError = true;
    this.errorMessage = message;
  }
}

export default AdvancedSettingsStore;